import { FC, useEffect } from "react";
import {
  BinInformation,
  BinSelectedInfo,
  EditAllotmentPoliciesLocationValues,
  LocationGrainTrackerValues,
  PrincipalInfoGT,
  StoragesTemplates,
} from "../../../../redux/slices/grainTracker/grainTrackerTypes";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import { theBinIsGood } from "../../create/GrainTrackerForm";
import { SkeletonListLayout } from "../../../userLocations/locationDetailsGrainTracker/skeletenoBin";
import { LoadingButton } from "@mui/lab";
import { stylesGW } from "../../grainWiseStylesMUIComponents";

type GrainTrackerLayoutFormParams = {
  t: (arg0: string) => string;
  setLocationGrainTracker: (graintracker: any) => void;
  setInformationByClicking: (
    binInformation: any,
    principalInfoGT: PrincipalInfoGT,
    principalInfoGTCopy: PrincipalInfoGT,
    token: string,
  ) => void;
  binCreation: (token: string) => void;
  selectBinIndex: (index: number) => void;
  locationGrainTrackerValues: LocationGrainTrackerValues;
  principalInfoGT: PrincipalInfoGT;
  principalInfoGTCopy: PrincipalInfoGT;
  selectIndexLayoutBin: number | undefined;
  listOfStoragesTemplates: {
    storagesTemplates: StoragesTemplates | undefined;
    loading: boolean;
    error: string;
  };
  editBins: EditAllotmentPoliciesLocationValues;
  token: string;
  selectedBin?: {
    values: undefined | BinSelectedInfo;
    loading: boolean;
    error: string;
  };
  isBinSelected?: boolean;
  removeSelectedBin?: () => void;
};

const GrainTrackerLayoutForm: FC<GrainTrackerLayoutFormParams> = ({
  t,
  setLocationGrainTracker,
  setInformationByClicking,
  binCreation,
  selectBinIndex,
  locationGrainTrackerValues,
  principalInfoGT,
  principalInfoGTCopy,
  selectIndexLayoutBin,
  listOfStoragesTemplates,
  editBins,
  token,
  selectedBin,
  isBinSelected,
  removeSelectedBin,
}) => {
  const { isAddingStorage, isEditingBin, storagesList, loading } =
    locationGrainTrackerValues;

  const handleListItemClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number,
    binInformation: any,
  ) => {
    setLocationGrainTracker({
      ...locationGrainTrackerValues,
      isEditingBin: true,
    });
    selectBinIndex(index);
    setInformationByClicking(
      binInformation,
      principalInfoGT,
      principalInfoGTCopy,
      token,
    );
  };

  const setToTrueEditAddStorage = () => {
    binCreation(token);
    selectBinIndex(storagesList.length);
  };

  const sorted = storagesList?.toSorted(
    (a: any, b: any) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const findTheSelectedBinIndex = () => {
    if (isBinSelected && selectedBin?.values) {
      const foundIndex = sorted.findIndex(
        (bin) => bin.id === selectedBin.values?.id,
      );

      return foundIndex !== -1;
    }
  };

  useEffect(() => {
    if (selectedBin?.values && isBinSelected) {
      handleListItemClick(
        {} as React.MouseEvent<HTMLDivElement, MouseEvent>,
        findTheSelectedBinIndex()
          ? (sorted.findIndex(
              (bin) => bin.id === selectedBin?.values?.id,
            ) as number)
          : 0,
        findTheSelectedBinIndex()
          ? (sorted.find(
              (bin) => bin.id === selectedBin?.values?.id,
            ) as BinInformation)
          : (sorted[0] as BinInformation),
      );
      setTimeout(() => {
        removeSelectedBin?.();
      }, 300);
    }
  }, []);

  return (
    <div className="layout-graintracker-container">
      <div className="layout-new-space">
        {t("Grain_tracker.new_storage_desc")}
      </div>
      <div className="divider-graintracker-layout" />

      <Box sx={stylesGW.GrainTrackerLayoutForm.layoutGraintrackerContainerBox}>
        {!loading ? (
          <List component="nav">
            {storagesList.length >= 1
              ? sorted.map((bins: BinInformation, indx) => {
                  return (
                    <ListItemButton
                      selected={selectIndexLayoutBin === indx}
                      key={bins.id}
                      disabled={
                        (selectIndexLayoutBin !== indx && isAddingStorage) ||
                        isEditingBin
                      }
                      onClick={(event) =>
                        handleListItemClick(event, indx, bins)
                      }
                    >
                      <ListItemText primary={bins.name} />
                      {!theBinIsGood(
                        bins.cerealTypeId,
                        bins.storageTemplateId,
                        bins.characteristics?.capacityMetricTonsWheatEq,
                      ) ? (
                        <ListItemIcon>
                          <DoneIcon />
                        </ListItemIcon>
                      ) : (
                        <ListItemIcon>
                          <ClearIcon />
                        </ListItemIcon>
                      )}
                    </ListItemButton>
                  );
                })
              : ""}
          </List>
        ) : (
          <>
            <List sx={stylesGW.GrainTrackerLayoutForm.list}>
              {SkeletonListLayout()}
            </List>
            <List sx={stylesGW.GrainTrackerLayoutForm.list}>
              {SkeletonListLayout()}
            </List>
          </>
        )}
        <LoadingButton
          loading={loading || editBins.editedLoading}
          sx={stylesGW.GrainTrackerLayoutForm.loadingButton}
          size="large"
          startIcon={<AddIcon />}
          disabled={
            locationGrainTrackerValues.locationInformation?.id === undefined ||
            isAddingStorage ||
            isEditingBin ||
            listOfStoragesTemplates.storagesTemplates?.data.length === 0 ||
            loading
          }
          onClick={setToTrueEditAddStorage}
        >
          {t("Grain_tracker.desc_add_storage_button")}
        </LoadingButton>
      </Box>
    </div>
  );
};

export default GrainTrackerLayoutForm;
